import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../ui/card";
import { toast } from "sonner";
import { TravelCostForm } from "./travel-costs/TravelCostForm";
import { TravelCostListItem } from "./travel-costs/TravelCostListItem";

const emptyForm = { city: "", price: "" };

export const AdminTravelCosts = () => {
  const [travelCosts, setTravelCosts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/travel-costs").then((r) => setTravelCosts(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (tc) => {
    setEditingId(tc.id);
    setForm({ city: tc.city, price: tc.price });
  };

  const submit = async () => {
    if (!form.city.trim() || form.price === "") {
      toast.error("Completa la ciudad/zona y el costo");
      return;
    }
    if (Number(form.price) < 0) {
      toast.error("El costo no puede ser negativo");
      return;
    }
    setSaving(true);
    const payload = { city: form.city.trim(), price: Number(form.price) };
    try {
      if (editingId) {
        await api.put(`/admin/travel-costs/${editingId}`, payload);
        toast.success("Zona actualizada");
      } else {
        await api.post("/admin/travel-costs", payload);
        toast.success("Zona agregada");
      }
      resetForm();
      await load();
    } catch {
      toast.error("Error al guardar la zona");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/travel-costs/${id}`);
      toast.success("Zona eliminada");
      await load();
    } catch {
      toast.error("Error al eliminar la zona");
    }
  };

  return (
    <Card className="p-6 space-y-6" data-testid="admin-travel-costs-panel">
      <h3 className="font-semibold">Viáticos por ciudad o zona</h3>
      <p className="text-sm text-muted-foreground">
        Configura el costo de viáticos para cada ciudad o zona. En el cotizador público, si el cliente
        escribe una ciudad de esta lista, el costo se sumará automáticamente al total. Si no coincide con
        ninguna, se le avisará que los viáticos se cotizarán por separado.
      </p>

      <div className="space-y-2">
        {travelCosts.length === 0 ? (
          <p data-testid="admin-travel-costs-empty" className="text-sm text-muted-foreground">
            Aún no has agregado ninguna ciudad o zona.
          </p>
        ) : (
          travelCosts.map((tc) => (
            <TravelCostListItem key={tc.id} tc={tc} onEdit={() => startEdit(tc)} onDelete={() => remove(tc.id)} />
          ))
        )}
      </div>

      <TravelCostForm
        form={form}
        setForm={setForm}
        editingId={editingId}
        saving={saving}
        onSubmit={submit}
        onCancel={resetForm}
      />
    </Card>
  );
};
